import { KeyRound, Briefcase, PoundSterling, Wrench, Building2, Lightbulb, CalendarCheck, FileSignature, Infinity as InfinityIcon } from 'lucide-react';
import { Reveal, SectionHeading } from './Reveal';

const STEPS = [
  {
    step: '01',
    title: 'We Lease Your Property',
    desc: 'You sign a Company Let Agreement with us, and we become your tenant.',
    Icon: KeyRound,
  },
  {
    step: '02',
    title: 'We House Trusted Guests',
    desc: 'The property hosts working professionals, families on vacation and other trusted guests needing short- to medium-term stays.',
    Icon: Briefcase,
  },
  {
    step: '03',
    title: 'You Get Paid — No Hassle',
    desc: 'Guaranteed rent every month, with full property management taken care of.',
    Icon: PoundSterling,
  },
];

const AGENT_POINTS = [
  'The agent continues to manage all major repairs and maintenance.',
  'We handle all minor maintenance issues and ensure the property remains in excellent condition.',
];

const DIRECT_POINTS = [
  'Major structural and appliance-related repairs remain your responsibility.',
  'We cover all minor fixes, cosmetic upkeep and day-to-day maintenance.',
  'We keep the property to a high, hotel-like standard with professional cleaning and routine inspections.',
];

const AGREEMENTS = [
  {
    title: '3–5 Year Contracts',
    desc: 'Guaranteed rent built in — enjoy secure, stress-free income for years.',
    Icon: CalendarCheck,
  },
  {
    title: 'Break Clauses & Rent Reviews',
    desc: 'We tailor agreements to fit your long-term investment goals.',
    Icon: FileSignature,
  },
  {
    title: 'Even Longer Terms Available',
    desc: 'We are open to extended agreements for greater financial stability.',
    Icon: InfinityIcon,
  },
];

const Panel = ({ testid, icon: Icon, title, subtitle, points, highlight }) => (
  <div
    data-testid={testid}
    className={`t-card hover-lift h-full rounded-2xl border p-10 sm:p-12 ${highlight ? 't-border-strong' : 't-border'}`}
  >
    <span className="t-chip flex h-12 w-12 items-center justify-center rounded-full">
      <Icon size={20} className="t-accent-text" />
    </span>
    <h3 className="font-display t-text mt-8 text-xl font-semibold sm:text-2xl">{title}</h3>
    <p className="mono-label t-muted mt-2">{subtitle}</p>
    <ul className="mt-8 space-y-5">
      {points.map((p, i) => (
        <li key={i} className="flex gap-4" data-testid={`${testid}-point-${i}`}>
          <span className="mt-2 h-1 w-4 shrink-0 bg-[#DCBC7F]" />
          <span className="t-text2 text-sm font-light leading-relaxed">{p}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function Process() {
  return (
    <>
      <section id="how-it-works" className="bg-[#1B6640] py-28 lg:py-40" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <SectionHeading label="How It Works" title="Three steps to effortless income" align="center" />
          <div className="mt-20 grid grid-cols-1 gap-12 md:grid-cols-3 md:gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.step} delay={0.12 * i}>
                <div className="group relative" data-testid={`step-card-${i}`}>
                  {i < STEPS.length - 1 && (
                    <div className="absolute left-1/2 top-10 hidden h-px w-full bg-[#DCBC7F]/15 md:block" aria-hidden="true" />
                  )}
                  <div className="relative mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[#DCBC7F]/30 bg-[#155533] transition-colors duration-500 group-hover:border-[#DCBC7F]">
                    <s.Icon size={24} className="text-[#DCBC7F]" />
                  </div>
                  <p className="font-display mt-8 text-center text-5xl font-medium text-[#DCBC7F]/30">{s.step}</p>
                  <h3 className="font-display mt-4 text-center text-xl font-semibold text-[#FCFBFA] sm:text-2xl">
                    {s.title}
                  </h3>
                  <p className="mx-auto mt-3 max-w-xs text-center text-sm font-light leading-relaxed text-[#C4D4C9]">
                    {s.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="maintenance" className="theme-light t-bg py-28 lg:py-40" data-testid="maintenance-section">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="max-w-2xl">
            <SectionHeading label="Property Maintenance & Upkeep" title="Clear responsibilities, whichever way you work" />
          </div>
          <div className="mt-16 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <Reveal>
              <Panel
                testid="maintenance-agent-panel"
                icon={Building2}
                title="Working Through an Agent"
                subtitle="Agent-led major repairs"
                points={AGENT_POINTS}
              />
            </Reveal>
            <Reveal delay={0.12}>
              <Panel
                testid="maintenance-direct-panel"
                icon={Wrench}
                title="Working Directly With Us"
                subtitle="Major repairs stay with you"
                points={DIRECT_POINTS}
                highlight
              />
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div
              data-testid="maintenance-pro-tip"
              className="t-card t-border-strong mt-6 flex items-start gap-5 rounded-2xl border p-8 sm:p-10"
            >
              <span className="t-chip flex h-12 w-12 shrink-0 items-center justify-center rounded-full">
                <Lightbulb size={20} className="t-accent-text" />
              </span>
              <div>
                <p className="mono-label t-accent-text">Pro Tip</p>
                <p className="t-text2 mt-3 max-w-3xl text-sm font-light leading-relaxed sm:text-base">
                  Unlike traditional tenants, we invest in keeping your property in top shape — making improvements
                  where needed to maintain a high-end feel for our clients.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="mt-28">
            <SectionHeading label="Flexibility" title="Long-term agreements, built around you" />
            <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-3">
              {AGREEMENTS.map((a, i) => (
                <Reveal key={a.title} delay={0.08 * i}>
                  <div
                    data-testid={`agreement-card-${i}`}
                    className="group t-card t-border hover-lift h-full rounded-2xl border p-8 sm:p-10"
                  >
                    <span className="t-chip flex h-12 w-12 items-center justify-center rounded-full">
                      <a.Icon size={20} className="t-accent-text" />
                    </span>
                    <h3 className="font-display t-text mt-8 text-xl font-semibold">{a.title}</h3>
                    <p className="t-text2 mt-3 text-sm font-light leading-relaxed">{a.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
